import { useEffect, useMemo, useState } from 'react'

const DEFAULT_ALL_LIMIT = 10000

export interface UsePaginationReturn {
  /** Current page size */
  pageSize: number | 'All'
  /** Set page size */
  setPageSize: (size: number | 'All') => void
  /** Current page number (1-indexed) */
  currentPage: number
  /** Set current page */
  setCurrentPage: (page: number) => void
  /** Offset for API request */
  offset: number
  /** Limit for API request */
  limit: number
  /** Total number of pages */
  totalPages: number
}

/**
 * Hook for managing list pagination state.
 * @param totalCount - Total number of items (used for calculating total pages)
 * @param allLimit - Limit to use when page size is "All" (defaults to 10000)
 */
export function usePagination(
  totalCount: number,
  allLimit: number = DEFAULT_ALL_LIMIT,
): UsePaginationReturn {
  const [pageSize, setPageSize] = useState<number | 'All'>(25)
  const [currentPage, setCurrentPage] = useState(1)

  const offset = useMemo(() => {
    if (pageSize === 'All') return 0
    return (currentPage - 1) * pageSize
  }, [currentPage, pageSize])

  const limit = useMemo(() => {
    if (pageSize === 'All') return allLimit
    return pageSize
  }, [pageSize, allLimit])

  const totalPages = useMemo(() => {
    if (pageSize === 'All') return 1
    return Math.ceil(totalCount / pageSize)
  }, [totalCount, pageSize])

  useEffect(() => {
    setCurrentPage(1)
  }, [pageSize])

  useEffect(() => {
    if (pageSize === 'All') return
    const maxPage = Math.max(1, Math.ceil(totalCount / pageSize))
    if (currentPage > maxPage) {
      setCurrentPage(maxPage)
    }
  }, [totalCount, pageSize, currentPage])

  return {
    pageSize,
    setPageSize,
    currentPage,
    setCurrentPage,
    offset,
    limit,
    totalPages,
  }
}
